import type { Database } from 'sql.js';

function tableHasColumn(db: Database, table: string, column: string): boolean {
  const stmt = db.prepare(`PRAGMA table_info(${table})`);
  while (stmt.step()) {
    const row = stmt.getAsObject() as Record<string, unknown>;
    if (row.name === column) {
      stmt.free();
      return true;
    }
  }
  stmt.free();
  return false;
}

function tableExists(db: Database, table: string): boolean {
  const stmt = db.prepare(`SELECT 1 FROM sqlite_master WHERE type = 'table' AND name = ? LIMIT 1`);
  stmt.bind([table]);
  const ok = stmt.step();
  stmt.free();
  return ok;
}

function addColumnIfMissing(db: Database, table: string, column: string, definition: string): boolean {
  if (!tableExists(db, table) || tableHasColumn(db, table, column)) return false;
  db.run(`ALTER TABLE ${table} ADD COLUMN ${column} ${definition}`);
  return true;
}

/** ALTER-only upgrades for IndexedDB DBs opened before snapshot reseed. */
export function migrateLocalSqliteSchema(db: Database): boolean {
  let changed = false;
  if (addColumnIfMissing(db, 'beneficiarios', 'tipo_documento_public_id', 'TEXT')) changed = true;
  if (addColumnIfMissing(db, 'beneficiarios', 'telefono1', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'beneficiarios', 'telefono2', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'beneficiarios', 'whatsapp', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'entrega_remisiones', 'foto_media_id', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'entrega_remisiones', 'remanente', 'INTEGER NOT NULL DEFAULT 0')) {
    db.run(
      `UPDATE entrega_remisiones SET remanente = MAX(0, cantidad_solicitadas - cantidad_entregadas)`,
    );
    changed = true;
  }
  if (addColumnIfMissing(db, 'entrega_remisiones', 'remanente_bolsas', 'INTEGER NOT NULL DEFAULT 0')) {
    db.run(
      `UPDATE entrega_remisiones SET remanente_bolsas = MAX(0, cantidad_bolsas_solicitadas - cantidad_bolsas_entregadas)`,
    );
    changed = true;
  }
  if (addColumnIfMissing(db, 'entrega_participantes', 'tipo_documento_public_id', 'TEXT')) changed = true;
  if (addColumnIfMissing(db, 'entrega_participantes', 'telefono1', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'entrega_participantes', 'telefono2', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'entrega_participantes', 'whatsapp', "TEXT NOT NULL DEFAULT ''")) changed = true;
  for (const column of [
    'cedula_beneficiario_front_media_id',
    'cedula_beneficiario_back_media_id',
    'cedula_autorizado_front_media_id',
    'cedula_autorizado_back_media_id',
    'documento_autorizacion_media_id',
  ]) {
    if (addColumnIfMissing(db, 'entrega_participantes', column, "TEXT NOT NULL DEFAULT ''")) changed = true;
  }
  if (addColumnIfMissing(db, 'entrega_participantes', 'entrega_bolsa', "TEXT NOT NULL DEFAULT 'no'")) {
    if (tableExists(db, 'entrega_participante_producto') && tableExists(db, 'productos_entrega')) {
      db.run(
        `UPDATE entrega_participantes SET entrega_bolsa = 'si'
         WHERE public_id IN (
           SELECT epp.participante_public_id
           FROM entrega_participante_producto epp
           JOIN productos_entrega pe ON pe.public_id = epp.producto_public_id
           WHERE UPPER(pe.codigo) = 'BOLSA'
         )`,
      );
    }
    changed = true;
  }
  if (addColumnIfMissing(db, 'reposiciones_productos', 'observaciones', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (!tableExists(db, 'punto_entrega_item')) {
    db.run(`CREATE TABLE punto_entrega_item (
      punto_entrega_public_id TEXT NOT NULL,
      item_entrega_public_id TEXT NOT NULL,
      PRIMARY KEY (punto_entrega_public_id, item_entrega_public_id)
    )`);
    db.run(`CREATE INDEX IF NOT EXISTS idx_punto_entrega_item_item ON punto_entrega_item (item_entrega_public_id)`);
    changed = true;
  }
  if (addColumnIfMissing(db, 'ciclos', 'fecha_inicio', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'ciclos', 'fecha_fin', "TEXT NOT NULL DEFAULT ''")) changed = true;
  if (addColumnIfMissing(db, 'entregas_paquetes', 'ciclo_public_id', "TEXT NOT NULL DEFAULT ''")) {
    db.run(
      `UPDATE entregas_paquetes SET ciclo_public_id = (
         SELECT p.ciclo_public_id FROM entrega_participantes p
         WHERE p.entrega_public_id = entregas_paquetes.public_id
         ORDER BY p.id ASC LIMIT 1
       )
       WHERE ciclo_public_id = ''
         AND EXISTS (SELECT 1 FROM entrega_participantes p WHERE p.entrega_public_id = entregas_paquetes.public_id)`,
    );
    changed = true;
  }
  const junctionTables = [
    [`item_entrega_ciclo`, `item_entrega_public_id TEXT NOT NULL, ciclo_public_id TEXT NOT NULL, PRIMARY KEY (item_entrega_public_id, ciclo_public_id)`],
    [`nutricionista_item`, `nutricionista_public_id TEXT NOT NULL, item_entrega_public_id TEXT NOT NULL, PRIMARY KEY (nutricionista_public_id, item_entrega_public_id)`],
    [`producto_entrega_item`, `producto_entrega_public_id TEXT NOT NULL, item_entrega_public_id TEXT NOT NULL, PRIMARY KEY (producto_entrega_public_id, item_entrega_public_id)`],
    [`producto_reposicion_item`, `producto_reposicion_public_id TEXT NOT NULL, item_entrega_public_id TEXT NOT NULL, PRIMARY KEY (producto_reposicion_public_id, item_entrega_public_id)`],
    [`beneficiario_item`, `beneficiario_public_id TEXT NOT NULL, item_entrega_public_id TEXT NOT NULL, PRIMARY KEY (beneficiario_public_id, item_entrega_public_id)`],
  ] as const;
  for (const [table, cols] of junctionTables) {
    if (!tableExists(db, table)) {
      db.run(`CREATE TABLE ${table} (${cols})`);
      changed = true;
    }
  }
  db.run(`CREATE INDEX IF NOT EXISTS idx_item_entrega_ciclo_ciclo ON item_entrega_ciclo (ciclo_public_id)`);
  db.run(`CREATE INDEX IF NOT EXISTS idx_nutricionista_item_item ON nutricionista_item (item_entrega_public_id)`);
  db.run(`CREATE INDEX IF NOT EXISTS idx_producto_entrega_item_item ON producto_entrega_item (item_entrega_public_id)`);
  db.run(`CREATE INDEX IF NOT EXISTS idx_producto_reposicion_item_item ON producto_reposicion_item (item_entrega_public_id)`);
  db.run(`CREATE INDEX IF NOT EXISTS idx_beneficiario_item_item ON beneficiario_item (item_entrega_public_id)`);
  return changed;
}
